import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

const POLL_MS = 15_000;
const LIVE_WINDOW_S = 5 * 60; // event within last 5 min = "Live"

export default function PMSyncStatusDot() {
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = async () => {
    setLoading(true);
    try {
      const [h, w] = await Promise.all([
        api.pmHealth(),
        api.pmWebhookStatus().catch(() => null),
      ]);
      setHealth(h);
      setWebhook(w);
    } catch (e) {
      setHealth({ ok: false, error: e.message || 'Health check failed' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    check();
    const t = setInterval(check, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const ok = health?.ok;
  // Server-computed seconds-since (no client-side TZ math)
  const secondsSince = webhook?.secondsSinceLastEvent;
  const isLive =
    ok && secondsSince != null && secondsSince >= 0 && secondsSince < LIVE_WINDOW_S;

  const dotClass = loading
    ? 'bg-neutral-500 animate-pulse'
    : isLive
    ? 'bg-emerald-400 animate-pulse'
    : ok
    ? 'bg-emerald-400'
    : 'bg-rose-500';

  const stateLabel = loading
    ? '…'
    : isLive
    ? 'Live'
    : ok
    ? 'Online'
    : 'Offline';

  const stateColor = loading
    ? 'text-neutral-400'
    : isLive
    ? 'text-emerald-400'
    : ok
    ? 'text-emerald-400'
    : 'text-rose-400';

  const tooltipLines = [
    isLive
      ? `Live · last webhook ${webhook?.lastEventType || '?'}`
      : ok
      ? `PM connected · ${health.projectCount ?? '?'} projects`
      : `PM offline${health?.statusCode ? ` (HTTP ${health.statusCode})` : ''}`,
    health?.urlHost ? `Host: ${health.urlHost}` : null,
    webhook?.count24h != null ? `${webhook.count24h} webhooks in last 24h` : null,
    'Click to manage sync',
  ].filter(Boolean);

  return (
    <button
      onClick={() => navigate('/')}
      title={tooltipLines.join('\n')}
      data-testid="pm-sync-status-dot"
      className="w-full mt-2 flex items-center gap-2 px-2 py-1.5 rounded-md border border-neutral-900 hover:bg-neutral-900 transition-colors text-left"
    >
      <span className={`h-2 w-2 rounded-full shrink-0 ${dotClass}`} />
      <span className="text-[10px] tracking-wider uppercase text-neutral-500 flex-1 truncate">
        PM Sync
      </span>
      <span className={`text-[10px] font-medium ${stateColor}`}>{stateLabel}</span>
    </button>
  );
}
